import { type ReactNode } from 'react';

import { readField, selectOptions } from '@eneco/cms-types';
import RichText from '@eneco/components/RichText/RichText';
import { Box, RadioGroup, RadioButton, Stack } from '@eneco/sparky';
import { Control, Controller, Path } from 'react-hook-form';

import { EnergyUsagePurposeContentField, EnergyUsagePurposeList, EnergyUsagePurposeListField } from './types';

interface FormFieldsWithEnergyUsagePurpose {
  energyUsagePurpose?: string;
}

interface EnergyUsagePurposeSelectorProps<
  T extends FormFieldsWithEnergyUsagePurpose = FormFieldsWithEnergyUsagePurpose,
> {
  control: Control<T>;
  energyUsagePurposeList: EnergyUsagePurposeListField | EnergyUsagePurposeList;
  content?: EnergyUsagePurposeContentField;
  direction?: 'row' | 'column';
}

const toRadioGroupValue = (value?: string) => (value && value !== 'none' ? value : '');

const EnergyUsagePurposeSelector = <T extends FormFieldsWithEnergyUsagePurpose = FormFieldsWithEnergyUsagePurpose>({
  control,
  energyUsagePurposeList,
  content,
  direction = 'row',
}: EnergyUsagePurposeSelectorProps<T>) => {
  const list = readField<EnergyUsagePurposeList>(energyUsagePurposeList);

  const options = selectOptions(list?.options);

  if (!list || !options.length) {
    return null;
  }

  const hintFromList = list.hint;
  const contentHtml = content?.value;
  const hint: ReactNode =
    contentHtml || hintFromList ? (
      <>
        {contentHtml ? <RichText html={contentHtml} /> : null}
        {hintFromList}
      </>
    ) : undefined;

  return (
    <Box paddingTop="3">
      <Stack direction="column" gap="3">
        <Controller
          control={control}
          name={'energyUsagePurpose' as Path<T>}
          render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
            <RadioGroup
              ref={ref}
              name={name}
              direction={direction}
              value={toRadioGroupValue(value as string)}
              onValueChange={onChange as (nextValue: string) => void}
              label={list.label ?? ''}
              hint={hint}
              error={error?.message}
              isOptional={!list.requiredMessage}>
              {options.map(option => (
                <RadioButton value={option.value} key={option.value}>
                  {option.label}
                </RadioButton>
              ))}
            </RadioGroup>
          )}
        />
      </Stack>
    </Box>
  );
};

export { EnergyUsagePurposeSelector };
export default EnergyUsagePurposeSelector;
